import React, { useCallback, useContext } from 'react';
import styled from 'astroturf/react';

import { Button } from 'js/design_system/Button';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import {
  FollowableUser,
  NewsType,
  PostReason,
  PaperUpdateReason,
} from 'js/social/shared/types';
import { FeedContext } from 'js/social/contexts/feed';
import {
  createUserFollowing,
  deleteUserFollowing,
} from 'js/social/api/user_following_api';
import { AuthorHeader } from './AuthorHeader';
import { ImpressionPropsGenerator } from 'js/social/ImpressionHelper';
import _ResearcherListWrapper from "ResearcherList-ResearcherListWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/ResearcherList.tsx?styleId=ResearcherListWrapper";
import _UserRow from "ResearcherList-UserRow.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/ResearcherList.tsx?styleId=UserRow";
import _AuthorWrapper from "ResearcherList-AuthorWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/ResearcherList.tsx?styleId=AuthorWrapper";
import _FollowButton from "ResearcherList-FollowButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/ResearcherList.tsx?styleId=FollowButton";


const ResearcherListWrapper = /*#__PURE__*/styled(ImpressionWatcher, null, {
  displayName: "ResearcherListWrapper",
  styles: _ResearcherListWrapper
});

const UserRow = /*#__PURE__*/styled("div", null, {
  displayName: "UserRow",
  styles: _UserRow
});

const AuthorWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "AuthorWrapper",
  styles: _AuthorWrapper
});

const FollowButton = /*#__PURE__*/styled(Button, null, {
  displayName: "FollowButton",
  styles: _FollowButton
});

function getSubtitles(user: FollowableUser): string[] | undefined {
  const subtitles = [user.followers > 0 && `${user.followers} followers`];
  if (user.openalexAuthor && user.openalexAuthor.citedByCount > 0) {
    subtitles.unshift(`${user.openalexAuthor.citedByCount} citations`);
  } else {
    subtitles.push(
      user.worksCount != null &&
        user.worksCount > 0 &&
        `${user.worksCount} papers`,
    );
  }

  const finalSubtitles = subtitles.filter(Boolean) as string[];
  if (finalSubtitles.length === 0) {
    return undefined;
  } else {
    return [finalSubtitles.join(' • ')];
  }
}

type Props = {
  users: FollowableUser[];
  newsId: number | string;
  newsType: NewsType;
  reason: PostReason | PaperUpdateReason;
  impressionPropsGen: ImpressionPropsGenerator;
};

export const ResearcherList = ({
  users,
  newsId,
  newsType,
  reason,
  impressionPropsGen,
}: Props) => {
  const {
    userActions: { setUserFollowing },
  } = useContext(FeedContext);

  const followUser = useCallback(
    async (userId: number) => {
      await createUserFollowing(userId, 'social_autogroup_tooltip', {
        item_id: newsId,
        item_type: newsType,
        item_reason: reason.type,
      });
      setUserFollowing(userId, true);
    },
    [newsId, newsType, reason, setUserFollowing],
  );

  const unfollowUser = useCallback(
    async (userId: number) => {
      await deleteUserFollowing(userId);
      setUserFollowing(userId, false);
    },
    [setUserFollowing],
  );

  return (
    <ResearcherListWrapper
      {...impressionPropsGen()()('researcher-list-tooltip')}
    >
      {users.map((user) => (
        <UserRow key={user.id}>
          <AuthorWrapper>
            <AuthorHeader
              kind="user"
              user={user}
              showTimestamp={false}
              showAvatar={true}
              size="sm"
              extraSubtitles={getSubtitles(user)}
              impressionPropsGen={() => () => () => ({
                source: 'researcher_list',
                active: false,
              })}
            />
          </AuthorWrapper>
          {user.following ? (
            <FollowButton
              size="xs"
              icon="check"
              onClick={() => unfollowUser(user.id)}
            >
              Following
            </FollowButton>
          ) : (
            <FollowButton
              size="xs"
              icon="add"
              onClick={() => followUser(user.id)}
            >
              Follow
            </FollowButton>
          )}
        </UserRow>
      ))}
    </ResearcherListWrapper>
  );
};
